import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const Products = () => {
  const location = useLocation();

  useEffect(() => {
    // If there's a hash in the URL, scroll to that section
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If no hash, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-[#F8F6F2]">
      {/* Hero Section */}
      <section className="pt-20 sm:pt-24 md:pt-32">
        <div className="container mx-auto px-4 sm:px-6 mb-8 sm:mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="max-w-2xl"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-[#2C231E] mb-4 sm:mb-6 leading-tight">
            HANDCRAFTED FORMS. NATURAL MATERIALS. SCALEABLE PRODUCTION.
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-[#2C231E]/70 font-lora font-light leading-relaxed">
            We offer design-forward furniture, decor, and serveware — fully customizable and built for retail, hospitality, and collaborative projects.
            </p>
          </motion.div>
        </div>

        <div className="w-full h-[50vh] sm:h-[60vh] md:h-[70vh] relative">
          <img
            src="/images/productsintro.jpg"
            alt="Elegant Furniture"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 sm:py-24 md:py-32 bg-[#F1F5E9]">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-xl sm:text-2xl md:text-3xl font-light text-[#2C231E] mb-6 sm:mb-8 leading-relaxed">
              "THE BEAUTY IN IMPERFECTIONS REVEALS THE STORY OF ARTISTRY AND CRAFTSMANSHIP"
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-[#2C231E]/80 leading-relaxed">
              Each piece carries a tale of meticulous attention to detail and a passion for natural materials. 
              Our artisans embrace the unique characteristics of wood, celebrating its natural grain patterns 
              and organic variations. This dedication to authenticity results in furniture that's not just 
              functional, but a testament to timeless craftsmanship.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Furniture Category Section */}
      <section id="furniture" className="py-16 sm:py-24 md:py-32">
        <div className="container mx-auto px-4 sm:px-6">
          {/* Main Furniture Intro Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 lg:gap-16 mb-16 sm:mb-24 md:mb-32">
            <div className="lg:col-span-4 flex flex-col justify-start lg:pt-16 order-1">
              <h2 className="text-3xl sm:text-4xl font-playfair font-light text-[#2C231E] mb-6 sm:mb-8">FURNITURE</h2>
              <div className="space-y-3 text-sm sm:text-base text-[#2C231E]/70 font-lora font-light leading-relaxed">
                <p>
                  We craft premium solid wood furniture in combinations of marble, metal, and rope — all hand-finished for durability and timeless design.
                </p>
                <p>
                  Crafted in Acacia, Mango, Oak. Finish & size customizable.
                </p>
              </div>
            </div>
            <div className="lg:col-span-8 order-2">
              <img
                src="/images/furnitureinto.jpg"
                alt="Furniture Collection"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Accent Furniture - Images Left */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 sm:mb-24 md:mb-32">
            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-1 aspect-h-1 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/accent1.jpg"
                    alt="Accent Furniture 1"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-1 aspect-h-1 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/accent2.jpg"
                    alt="Accent Furniture 2"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-1 aspect-h-1 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/accent3.jpg"
                    alt="Accent Furniture 3"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-3 flex flex-col justify-start order-1 lg:order-2"
            >
              <h3 className="text-xl sm:text-2xl font-playfair font-light text-[#2C231E] mb-3 sm:mb-4">ACCENT FURNITURE</h3>
              <p className="text-sm sm:text-base text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Transform overlooked corners into captivating vignettes. Our accent pieces — from sculptural side tables 
                to versatile consoles — are designed to be both conversation starters and functional anchors in your space. 
                Each piece showcases the natural beauty of wood through clean lines and thoughtful proportions.
              </p>
            </motion.div>
          </div>

          {/* Bar Furniture - Images Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 sm:mb-24 md:mb-32">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-3 flex flex-col justify-start order-1"
            >
              <h3 className="text-xl sm:text-2xl font-playfair font-light text-[#2C231E] mb-3 sm:mb-4">BAR FURNITURE</h3>
              <p className="text-sm sm:text-base text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Elevate your entertaining experience with our bar collection. From sleek counters to statement cabinets, 
                each piece is engineered for both beauty and practicality. Featuring dovetail joinery and premium hardware, 
                these pieces are built to withstand the demands of hospitality while maintaining their elegant presence.
              </p>
            </motion.div>

            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 order-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/bar1.jpg"
                    alt="Bar Furniture 1"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/bar2.jpg"
                    alt="Bar Furniture 2"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/bar3.jpg"
                    alt="Bar Furniture 3"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>
          </div>

          {/* Outdoor Furniture - Images Left */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 sm:mb-24 md:mb-32">
            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/outdoor1.jpg"
                    alt="Outdoor Furniture 1"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/outdoor2.jpg"
                    alt="Outdoor Furniture 2"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/outdoor3.jpg"
                    alt="Outdoor Furniture 3"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-3 flex flex-col justify-start order-1 lg:order-2"
            >
              <h3 className="text-xl sm:text-2xl font-playfair font-light text-[#2C231E] mb-3 sm:mb-4">OUTDOOR FURNITURE</h3>
              <p className="text-sm sm:text-base text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Bring the comfort of indoor living to your outdoor sanctuary. Our weather-resistant collection features 
                teak and treated hardwoods, designed to age gracefully in outdoor settings. From cozy lounge sets to 
                dining ensembles, each piece is crafted to withstand the elements while maintaining its natural elegance.
              </p>
            </motion.div>
          </div>

          {/* Kids & Pet Furniture - Images Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 sm:mb-24 md:mb-32">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-3 flex flex-col justify-start"
            >
              <h3 className="text-xl sm:text-2xl font-playfair font-light text-[#2C231E] mb-3 sm:mb-4">KIDS & PET FURNITURE</h3>
              <p className="text-sm sm:text-base text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Create a space that celebrates the unique personalities of your children and pets. Our furniture collection 
                is designed to be both functional and playful, featuring soft edges and non-toxic finishes. Each piece is 
                crafted with care, ensuring durability and comfort for years to come.
              </p>
            </motion.div>

            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/pet1.jpg"
                    alt="Kids & Pet Furniture 1"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/pet2.jpg"
                    alt="Kids & Pet Furniture 2"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/pet3.jpg"
                    alt="Kids & Pet Furniture 3"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVEWARE CATEGORY */}
      <section id="serveware" className="py-16 sm:py-24 md:py-32 bg-[#F8F6F2]">
        <div className="container mx-auto px-4 sm:px-6">
          {/* Main Serveware Intro Section - Image Left */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 lg:gap-16 mb-16 sm:mb-24 md:mb-32">
            <div className="lg:col-span-8 order-2 lg:order-1">
              <img
                src="/images/servewareintro.jpg"
                alt="Serveware Collection"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="lg:col-span-4 flex flex-col justify-start lg:pt-16 order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-playfair font-light text-[#2C231E] mb-6 sm:mb-8">SERVEWARE & KITCHENWARE</h2>
              <p className="text-sm sm:text-base text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Discover our collection of handcrafted serving essentials that turn every meal into a memorable occasion. 
                From breakfast nooks to dinner parties, each piece is designed to celebrate the art of presentation while 
                honoring the natural beauty of wood & marble.
              </p>
            </div>
          </div>

          {/* Trays & Platters - Images Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 sm:mb-24 md:mb-32">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-3 flex flex-col justify-start order-1"
            >
              <h3 className="text-xl sm:text-2xl font-playfair font-light text-[#2C231E] mb-3 sm:mb-4">TRAYS & PLATTERS</h3>
              <p className="text-sm sm:text-base text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Flat or curved edges, perfect for serving or shelf styling.
              </p>
            </motion.div>

            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 order-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/tray1.jpg"
                    alt="Dining Plates 1"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/tray2.jpg"
                    alt="Dining Plates 2"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/tray3.jpg"
                    alt="Dining Plates 3"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>
          </div>

          {/* Bowls & Cake Stands - Images Left */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 sm:mb-24 md:mb-32">
            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/bowl1.jpg"
                    alt="Serving Bowls 1"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/bowl2.jpg"
                    alt="Serving Bowls 2"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/bowl3.jpg"
                    alt="Serving Bowls 3"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-3 flex flex-col justify-start order-1 lg:order-2"
            >
              <h3 className="text-xl sm:text-2xl font-playfair font-light text-[#2C231E] mb-3 sm:mb-4">BOWLS & CAKE STANDS</h3>
              <p className="text-sm sm:text-base text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Elevate your culinary presentation with our collection of artisanal serving boards. Each piece is carefully crafted from hardwoods & marble with inlay of metal, featuring organic edges and food-safe finishes. Perfect for charcuterie, cheese platters, or as striking centerpieces, these boards bring rustic elegance to any dining experience.
              </p>
            </motion.div>
          </div>

          {/* Boards & Coasters - Images Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 sm:mb-24 md:mb-32">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-3 flex flex-col justify-start order-1"
            >
              <h3 className="text-xl sm:text-2xl font-playfair font-light text-[#2C231E] mb-3 sm:mb-4">BOARDS & COASTERS</h3>
              <p className="text-sm sm:text-base text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Chopping, cheese, or charcuterie — custom shapes available.
              </p>
            </motion.div>

            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 order-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/board1.jpg"
                    alt="Serving Platters 1"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/board2.jpg"
                    alt="Serving Platters 2"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/board3.jpg"
                    alt="Serving Platters 3"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Utility Category Section */}
      <section id="table-decor" className="py-16 sm:py-24 md:py-32">
        <div className="container mx-auto px-4 sm:px-6">
          {/* Main Utility Intro Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 lg:gap-16 mb-16 sm:mb-24 md:mb-32">
            <div className="lg:col-span-8">
              <img
                src="/images/utilityintro.jpg"
                alt="Utility Collection"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="lg:col-span-4 flex flex-col justify-start lg:pt-16">
              <h2 className="text-3xl sm:text-4xl font-playfair font-light text-[#2C231E] mb-6 sm:mb-8">TABLE DECOR & UTILITY</h2>
              <p className="text-sm sm:text-base text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Experience the art of mindful living through our collection of table accessories. Each piece is thoughtfully 
                designed to enhance your daily rituals, combining natural materials with contemporary functionality. From 
                serving essentials to desk accessories, discover items that make every moment more intentional.
              </p>
            </div>
          </div>

          {/* Candleholders - Images Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 sm:mb-24 md:mb-32">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-3 flex flex-col justify-start order-1"
            >
              <h3 className="text-xl sm:text-2xl font-playfair font-light text-[#2C231E] mb-3 sm:mb-4">CANDLEHOLDERS</h3>
              <p className="text-sm sm:text-base text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Illuminate your space with the warm glow of our handcrafted candleholders. From minimalist taper holders 
                to elaborate centerpieces, each design plays with light and shadow while celebrating the natural variations 
                in wood grain. Perfect for creating ambiance in any setting.
              </p>
            </motion.div>

            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 order-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/candle1.jpg"
                    alt="Decorative Panel 1"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/candle2.jpg"
                    alt="Decorative Panel 2"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/candle3.jpg"
                    alt="Decorative Panel 3"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>
          </div>

          {/* Holders & Utility Boxes - Images Left */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 sm:mb-24 md:mb-32">
            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-1 aspect-h-1 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/shelf1.jpg"
                    alt="Wall Mirror 1"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-1 aspect-h-1 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/shelf2.jpg"
                    alt="Wall Mirror 2"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-1 aspect-h-1 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/shelf3.jpg"
                    alt="Wall Mirror 3"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-3 flex flex-col justify-start order-1 lg:order-2"
            >
              <h3 className="text-2xl font-playfair font-light text-[#2C231E] mb-4">HOLDERS & UTILITY BOXES</h3>
              <p className="text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Merge organization with artistry through our collection of storage solutions. Each piece transforms everyday 
                storage into a display-worthy element, featuring precise joinery and thoughtful details that elevate both 
                wall-mounted and tabletop spaces.
              </p>
            </motion.div>
          </div>

          {/* Bath Sets & Office Accessories - Images Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 sm:mb-24 md:mb-32">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-3 flex flex-col justify-start order-1"
            >
              <h3 className="text-2xl font-playfair font-light text-[#2C231E] mb-4">BATH SETS & OFFICE ACCESSORIES</h3>
              <p className="text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Each piece combines functionality with elegant design, featuring smooth-sliding lids and precision joinery. Crafted from premium woods like Acacia and Mango with combination of marble and inlay, these accessories add a touch of sophistication to any desk or bathroom setting. Available in various finishes to match your interior decor.
              </p>
            </motion.div>

            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 order-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/office1.jpg"
                    alt="Serving Tray 1"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/office2.jpg"
                    alt="Serving Tray 2"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/office3.jpg"
                    alt="Serving Tray 3"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Wall Decor Category Section */}
      <section id="wall-decor" className="py-16 sm:py-24 md:py-32">
        <div className="container mx-auto px-4 sm:px-6">
          {/* Main Wall Decor Intro Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 lg:gap-16 mb-16 sm:mb-24 md:mb-32">
            <div className="lg:col-span-8 order-2 lg:order-1">
              <img
                src="/images/walldecorintro.jpg"
                alt="Wall Decor Collection"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="lg:col-span-4 flex flex-col justify-start lg:pt-16 order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-playfair font-light text-[#2C231E] mb-6 sm:mb-8">WALL DECOR</h2>
              <p className="text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Transform your walls into curated galleries with our collection of wooden & marble wall art and functional decor. Each piece is designed to create visual interest while serving a purpose, combining innovative design with traditional techniques to bring warmth and character to your vertical spaces.
              </p>
            </div>
          </div>

          {/* Mirrors & Photo Frames - Images Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 sm:mb-24 md:mb-32">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-3 flex flex-col justify-start order-1"
            >
              <h3 className="text-2xl font-playfair font-light text-[#2C231E] mb-4">MIRRORS & PHOTO FRAMES</h3>
              <p className="text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Elevate your space with our collection of artisanal mirrors and photo frames. Each piece is meticulously crafted from premium woods,
                featuring clean lines and precision joinery. From minimalist single frames to statement mirrors, our designs range from 
                bold geometric patterns to subtle classic styles.
              </p>
            </motion.div>

            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 order-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/frame1.jpg"
                    alt="Mirror 1"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/frame2.jpg"
                    alt="Mirror 2"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-4 aspect-h-5 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/frame3.jpg"
                    alt="Mirror 3"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>
          </div>

          {/* Wall Hooks & Shelves - Images Left */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-1 aspect-h-1 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/hook1.jpg"
                    alt="Photo Frame 1"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-1 aspect-h-1 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/hook2.jpg"
                    alt="Photo Frame 2"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
              >
                <div className="aspect-w-1 aspect-h-1 bg-[#E8E2DC] overflow-hidden">
                  <img
                    src="/images/hook3.jpg"
                    alt="Photo Frame 3"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-3 flex flex-col justify-start order-1 lg:order-2"
            >
              <h3 className="text-2xl font-playfair font-light text-[#2C231E] mb-4">WALL HOOKS & SHELVES</h3>
              <p className="text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Blend functionality with artistic expression through our wall-mounted solutions. From sculptural coat hooks 
                to floating shelves, each piece is engineered for both stability and style. Perfect for creating organized, 
                visually appealing moments in entryways, hallways, and living spaces.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products; 